import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, DatePicker } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWJ308Query } from './hooks/usePWJ308Query';
import type { ResultRow } from '../../services/pwj308Api';
import './PWJ308Form.css';

const { RangePicker } = DatePicker;

const PWJ308Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePWJ308Query();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [vendorFrom, setVendorFrom] = useState('');
  const [vendorTo, setVendorTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');

  const handleQuery = () => {
    runQuery({
      dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      vendorFrom: vendorFrom.trim() || undefined,
      vendorTo: vendorTo.trim() || undefined,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setDateRange([null, null]);
    setVendorFrom('');
    setVendorTo('');
    setDeptFrom('');
    setDeptTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const totalAmount = data.reduce((sum, r) => sum + (r.amount || 0), 0);
    win.document.write(`<!DOCTYPE html><html><head><title>研發廠商對帳單</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:3px 6px}th{background:#f0f0f0;text-align:center}.right{text-align:right}.total{font-weight:bold;background:#fafafa}@media print{body{margin:10mm}}</style></head><body>
<h2>研發廠商對帳單</h2>
<table><thead><tr><th>來源</th><th>單號</th><th>行號</th><th>部門</th><th>入庫日期</th><th>廠商代號</th><th>品項代號</th><th>規格</th><th>數量</th><th>金額</th><th>幣別</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.source}</td><td>${r.doc_no}</td><td class="right">${r.line_no}</td><td>${r.dept_name || r.dept_code}</td><td>${r.entry_date?.substring(0, 10) || ''}</td><td>${r.vendor_code}</td><td>${r.item_code}</td><td>${r.item_spec}</td><td class="right">${r.qty.toLocaleString()}</td><td class="right">${r.amount.toLocaleString()}</td><td>${r.currency}</td></tr>`).join('')}
<tr class="total"><td colspan="9" style="text-align:right">合計</td><td class="right">${totalAmount.toLocaleString()}</td><td></td></tr>
</tbody></table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ResultRow> = [
    { title: '來源', dataIndex: 'source', key: 'source', width: 70 },
    { title: '單號', dataIndex: 'doc_no', key: 'doc_no', width: 130 },
    { title: '行號', dataIndex: 'line_no', key: 'line_no', width: 60, align: 'right' as const },
    { title: '部門', dataIndex: 'dept_name', key: 'dept_name', width: 100, render: (v: string, r: ResultRow) => v || r.dept_code },
    { title: '入庫日期', dataIndex: 'entry_date', key: 'entry_date', width: 100, render: (v: string) => v?.substring(0, 10) || '' },
    { title: '廠商代號', dataIndex: 'vendor_code', key: 'vendor_code', width: 120 },
    { title: '品項代號', dataIndex: 'item_code', key: 'item_code', width: 120 },
    { title: '規格', dataIndex: 'item_spec', key: 'item_spec', width: 120, ellipsis: true },
    { title: '數量', dataIndex: 'qty', key: 'qty', width: 90, align: 'right' as const, render: (v: number) => v?.toLocaleString() },
    { title: '金額', dataIndex: 'amount', key: 'amount', width: 110, align: 'right' as const, render: (v: number) => v?.toLocaleString() },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 70 },
  ];

  return (
    <PermissionGuard module={Modules.PWJ308} action="query">
      <div className="pwj308-container">
        <div className="pwj308-header">
          <h2>研發廠商對帳單</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label className="pwj308-label">入庫日期範圍</label>
              <RangePicker
                value={dateRange}
                onChange={v => setDateRange(v ? [v[0], v[1]] : [null, null])}
                style={{ width: 240 }}
              />
            </div>
            <div>
              <label className="pwj308-label">廠商代號起</label>
              <Input
                value={vendorFrom}
                onChange={e => setVendorFrom(e.target.value)}
                placeholder="起始廠商"
                style={{ width: 130 }}
                allowClear
                maxLength={12}
              />
            </div>
            <div>
              <label className="pwj308-label">廠商代號迄</label>
              <Input
                value={vendorTo}
                onChange={e => setVendorTo(e.target.value)}
                placeholder="結束廠商"
                style={{ width: 130 }}
                allowClear
                maxLength={12}
              />
            </div>
            <div>
              <label className="pwj308-label">部門代號起</label>
              <Input
                value={deptFrom}
                onChange={e => setDeptFrom(e.target.value)}
                placeholder="起始部門"
                style={{ width: 100 }}
                allowClear
                maxLength={4}
              />
            </div>
            <div>
              <label className="pwj308-label">部門代號迄</label>
              <Input
                value={deptTo}
                onChange={e => setDeptTo(e.target.value)}
                placeholder="結束部門"
                style={{ width: 100 }}
                allowClear
                maxLength={4}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<ResultRow>
            columns={columns}
            dataSource={data}
            rowKey={(r) => `${r.source}-${r.doc_no}-${r.line_no}`}
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 1100 }}
            summary={(pageData) => {
              const total = pageData.reduce((sum, r) => sum + (r.amount || 0), 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={9} align="right">
                    <strong>本頁合計</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    <strong>{total.toLocaleString()}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} />
                </Table.Summary.Row>
              );
            }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWJ308Form;
